import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Grid, TextField, FormControlLabel, Switch, FormHelperText, Table, TableBody, TableCell, TableHead, TableRow, Paper, Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { Controller, useForm, useWatch } from "react-hook-form";
import { toast } from "react-toastify";

import AppForm from "components/Shared/AppForm";
import AppSwitch from "components/Shared/AppSwitch";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import FileDropzone from "components/Shared/FileDropzone";
import SignaturePadComponent from "utils/SignaturePadComponent";

import { useGetDDLAccountListQuery } from "store/services/accountApi";
import { useLazyGetDDLEntityListQuery } from "store/services/entityApi";
import { useLazyGetDDLSystemUserListQuery } from "store/services/userApi";
import { useGetDdlLoadingByDriverIdBranchWiseQuery } from "store/services/shipmentLoadingApi";

import { useDOUploadFile } from "hooks";
import { useAuth } from "hooks";

import { getDateTimeAuto } from "utils/dateFormats";

const timestamp = new Date()
    .toISOString()
    .replace(/[-:]/g, "")
    .replace("T", "_")
    .replace(/\..+/, "");

const INITIAL_STATE = {
    account: null,
    entity: null,
    loadingId: null,
    shipmentId: [],
    unloadingTime: getDateTimeAuto(),
    attachments: [],
    documentUrl: "",
    signature: "",
    receiveBy: "",
    receiverPhone: "",
    responsible: "",
    isActive: true,
    receiveInBehalf: false,
};

const DriverUnLoadingShipmentForm = ({
    isBusy = null,
    driverUnLoadingDetail = null,
    onSubmit = () => { },
}) => {
    const navigate = useNavigate();
    const { state } = useLocation();
    const { viewEntity, isAccountAdmin, isSystemAdmin, loggedInUser } = useAuth();
    const { handleUploadFiles } = useDOUploadFile();
    const signaturePadRef = useRef(null);
    const [signatureError, setSignatureError] = useState(false);

    // React Hook Form
    const {
        control,
        handleSubmit,
        setValue,
        formState: { errors, isSubmitted },
        reset,
        trigger,
    } = useForm({
        defaultValues: {
            ...INITIAL_STATE,
            entity: viewEntity?.entity?._id ? viewEntity.entity : null,
            account: viewEntity?.entity?.account?._id ? viewEntity.entity.account : null,
            loadingId: state?.loading || null,
            shipmentId: state?.shipments || [],
        },
    });

    // Watch form fields
    const _watchAccount = useWatch({ control, name: "account" });
    const _watchBranch = useWatch({ control, name: "branch" });
    const _watchLoadingId = useWatch({ control, name: "loadingId" });
    const _watchShipmentId = useWatch({ control, name: "shipmentId" });
    const _watchReceiveInBehalf = useWatch({ control, name: "receiveInBehalf" });

    // RTK Queries
    const { data: accounts, isLoading: loadingAccounts } = useGetDDLAccountListQuery();
    const [fetchEntities, { data: entities, isLoading: loadingEntities }] = useLazyGetDDLEntityListQuery();

    // New API for branch-based shipments
    const {
        data: branchShipmentLoadings,
        isLoading: loadingBranchShipmentLoadings,
    } = useGetDdlLoadingByDriverIdBranchWiseQuery(
        { driverId: loggedInUser?.typeRef },
        { skip: !loggedInUser?.typeRef }
    );

    const [fetchUsers, { data: userList, isLoading: loadingUsers, isFetching: fetchingUsers }] =
        useLazyGetDDLSystemUserListQuery();

    // Mapped users for responsible field
    const _mappedUsers = useMemo(
        () =>
            userList?.map((el) => ({
                label: el?.name,
                id: el?._id,
            })) || [],
        [userList]
    );

    // Extract unique branches from branch-wise API
    const availableBranches = useMemo(() => {
        if (!branchShipmentLoadings?.data) return [];

        const branches = new Map();
        branchShipmentLoadings.data.forEach(group => {
            if (group.destinationBranch && !branches.has(group.destinationBranch._id)) {
                branches.set(group.destinationBranch._id, {
                    _id: group.destinationBranch._id,
                    name: group.destinationBranch.name,
                    destinationBranchId: group.destinationBranchId,
                });
            }
        });

        return Array.from(branches.values());
    }, [branchShipmentLoadings]);


    // Extract all loadings (flattened from all branches)
    const allLoadingsFromBranchAPI = useMemo(() => {
        if (!branchShipmentLoadings?.data) return [];

        const loadings = [];
        branchShipmentLoadings.data.forEach(group => {
            group.loadings.forEach(loading => {
                loadings.push({
                    ...loading,
                    branchInfo: group.destinationBranch // Keep branch reference
                });
            });
        });

        return loadings;
    }, [branchShipmentLoadings]);



    // Derived data using useMemo for loading-based shipments
    const LoadedShipments = useMemo(() => {
        return allLoadingsFromBranchAPI.filter((load) => {
            const shipment = load.shipment;
            return (shipment.status === "Loaded" || shipment.status === "Return") &&
                !shipment.IsReturning;
        });
    }, [allLoadingsFromBranchAPI]);

    // Branch-based loaded shipments
    const BranchLoadedShipments = useMemo(() => {
        if (!_watchBranch || !branchShipmentLoadings?.data) return [];

        const selectedBranchGroup = branchShipmentLoadings.data.find((group) =>
            group?.destinationBranch?._id === _watchBranch._id
        );

        return selectedBranchGroup?.loadings || [];
    }, [branchShipmentLoadings, _watchBranch]);

    const selectedLoading = useMemo(
        () => LoadedShipments.find((load) => load?._id === _watchLoadingId?._id),
        [LoadedShipments, _watchLoadingId]
    );

    // Get available shipments based on branch or loading selection
    const availableShipments = useMemo(() => {
        // If branch is selected, use branch-based shipments
        if (_watchBranch && BranchLoadedShipments.length > 0) {
            const allBranchShipments = [];
            BranchLoadedShipments.forEach(loading => {
                // Each loading has a shipment object (not array)
                if (loading.shipment) {
                    allBranchShipments.push(loading.shipment);
                }
            });
            return allBranchShipments;
        }

        // If loading is selected, use loading-based shipments
        // If loading is selected, use loading-based shipments
        if (selectedLoading) {
            return selectedLoading.shipment ? [selectedLoading.shipment] : [];
        }

        // If shipments are passed from state
        if (state?.shipments?.length) {
            return state.shipments.map((s) => ({
                _id: s._id,
                shipmentId: s.shipmentId,
                status: s.status,
                IsReturning: s.IsReturning,
                customerId: s.customerId,
                vendor: s.vendor,
                receiver: s.receiver,
            }));
        }

        return [];
    }, [selectedLoading, BranchLoadedShipments, _watchBranch, state?.shipments]);

    const availableLoadings = useMemo(() => {
        if (_watchBranch && BranchLoadedShipments.length > 0) {
            return BranchLoadedShipments; // loadings for the selected branch
        }
        return LoadedShipments; // fallback to original loading API
    }, [_watchBranch, BranchLoadedShipments, LoadedShipments]);


    // Auto-populate loadingId and shipmentId from state
    useEffect(() => {
        if (state?.loading && allLoadingsFromBranchAPI?.length) {
            const matchingLoading = allLoadingsFromBranchAPI.find(
                (load) => load.loadingId === state.loading.loadingId
            );
            if (matchingLoading) {
                setValue("loadingId", matchingLoading, { shouldValidate: false });
            } else {
                setValue("loadingId", state.loading, { shouldValidate: false });
            }
        }
    }, [state, allLoadingsFromBranchAPI, setValue]);

    // Auto-populate shipmentId when loadingId changes (only if no branch selected)
    useEffect(() => {
        if (_watchLoadingId && selectedLoading && !state?.shipments && !_watchBranch) {
            const shipment = selectedLoading.shipment;
            if (shipment) {
                setValue("shipmentId", [{
                    _id: shipment._id,
                    shipmentId: shipment.shipmentId,
                    status: shipment.status,
                    IsReturning: shipment.IsReturning,
                    vendor: shipment.vendor,
                    receiver: shipment.receiver,
                }], { shouldValidate: false });
            }
        } else if (!_watchLoadingId && !state?.shipments && !_watchBranch) {
            setValue("shipmentId", [], { shouldValidate: false });
        }
    }, [_watchLoadingId, selectedLoading, state, setValue, _watchBranch]);

    // Auto-populate shipmentId when branch changes
    useEffect(() => {
        if (_watchBranch && BranchLoadedShipments.length > 0 && !state?.shipments) {
            const allBranchShipmentIds = [];
            BranchLoadedShipments.forEach((loading) => {
                if (loading.shipment) {
                    allBranchShipmentIds.push({
                        _id: loading.shipment._id,
                        shipmentId: loading.shipment.shipmentId,
                        status: loading.shipment.status,
                        IsReturning: loading.shipment.IsReturning,
                        customerId: loading.shipment.customerId,
                        vendor: loading.shipment.vendor,
                        receiver: loading.shipment.receiver,
                    });
                }
            });

            setValue("shipmentId", allBranchShipmentIds, { shouldValidate: false });

            // 👇 also set loadingId to the first available loading
            setValue("loadingId", BranchLoadedShipments[0], { shouldValidate: true });
        } else if (!_watchBranch && !state?.shipments) {
            setValue("shipmentId", [], { shouldValidate: false });
            setValue("loadingId", null, { shouldValidate: false });
        }
    }, [_watchBranch, BranchLoadedShipments, state, setValue]);


    // Fetch entities when account changes
    useEffect(() => {
        if (isAccountAdmin || isSystemAdmin) {
            fetchEntities({
                account: _watchAccount?._id ?? viewEntity?.entity?.account?._id,
            });
        }
    }, [_watchAccount?._id, fetchEntities, isAccountAdmin, isSystemAdmin, viewEntity?.entity?.account]);

    // Fetch users when entity changes
    useEffect(() => {
        if (viewEntity?.entity?._id && !driverUnLoadingDetail) {
            fetchUsers({ entity: viewEntity.entity._id });
        }
    }, [viewEntity?.entity?._id, fetchUsers, driverUnLoadingDetail]);

    // Reset form when editing
    useEffect(() => {
        if (!driverUnLoadingDetail) return;
        reset({
            ...INITIAL_STATE,
            ...driverUnLoadingDetail,
            unloadingTime: driverUnLoadingDetail?.unloadingTime
                ? new Date(driverUnLoadingDetail.unloadingTime).toISOString().slice(0, 16)
                : getDateTimeAuto(),
            shipmentId:
                driverUnLoadingDetail?.shipmentId?.map((item) => ({
                    _id: item.shipment._id,
                    shipmentId: item.shipment.shipmentId,
                    status: item.shipment.status,
                    IsReturning: item.shipment.IsReturning,
                    customerId: item.shipment.customerId,
                    vendor: item.shipment.vendor,
                    receiver: item.shipment.receiver,
                })) || [],
            account:
                driverUnLoadingDetail?.account?._id
                    ? driverUnLoadingDetail.account
                    : null,
            entity:
                driverUnLoadingDetail?.entity?._id ? driverUnLoadingDetail.entity : null,
            loadingId: driverUnLoadingDetail?.loadingId
                ? { loadingId: driverUnLoadingDetail.loadingId }
                : null,
            responsible: driverUnLoadingDetail?.responsible
                ? { id: driverUnLoadingDetail.responsible._id, label: driverUnLoadingDetail.responsible.name }
                : null,
        });

        const accountId =
            driverUnLoadingDetail?.account?._id ||
            viewEntity?.entity?.account?._id ||
            null;
        if (accountId && (isAccountAdmin || isSystemAdmin)) {
            fetchEntities({ account: accountId });
        }

        const entityId =
            driverUnLoadingDetail?.entity?._id || viewEntity?.entity?._id || null;
        if (entityId) {
            fetchUsers({ entity: entityId });
        }
    }, [
        reset,
        driverUnLoadingDetail,
        fetchEntities,
        fetchUsers,
        isAccountAdmin,
        isSystemAdmin,
        viewEntity?.entity,
    ]);

    // Set responsible user for drivers
    useEffect(() => {
        if (loggedInUser && !driverUnLoadingDetail) {
            const responsibleUser = {
                label: loggedInUser.name,
                id: loggedInUser._id,
            };
            setValue("responsible", responsibleUser, { shouldValidate: true });
        }
    }, [loggedInUser, setValue, driverUnLoadingDetail]);

    // Convert data URL to file for signature
    const convertDataUrlToFile = (dataUrl, filename) => {
        const arr = dataUrl.split(",");
        const mime = arr[0].match(/:(.*?);/)[1];
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);

        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }

        return new File([u8arr], filename, { type: mime });
    };

    // Form submission handler
    const handleOnDriverUnLoadingSubmit = async (data) => {
        const signatureDataUrl = signaturePadRef?.current?.getSignature();
        if (!signatureDataUrl) {
            setSignatureError(true);
            return;
        }
        setSignatureError(false);

        const signatureFile = convertDataUrlToFile(
            signatureDataUrl,
            `driver_unloading_signature_${timestamp}.png`
        );

        const shipmentId = data.shipmentId.map((shipment) => ({
            shipment: shipment._id || shipment.shipmentId
        }));

        // Separate existing and new attachments
        const [existingAttachments, newAttachments] = data?.attachments?.reduce(
            ([pass, fail], el) =>
                el?.publicId || el?.url ? [[...pass, el], fail] : [pass, [...fail, el]],
            [[], []]
        );

        try {
            const uploadPromises = [];

            // Upload signature
            uploadPromises.push(
                handleUploadFiles([signatureFile], {
                    folderName: "DO_FOLDER_NAME.UNLOADING_SIGNATURE",
                    entity: viewEntity?.entity?._id,
                    account: viewEntity?.entity?.account?._id,
                })
            );

            // Upload new attachments
            if (newAttachments?.length) {
                uploadPromises.push(
                    handleUploadFiles(newAttachments, {
                        folderName: "SHIPMENT_UNLOADING",
                        entity: viewEntity?.entity?._id,
                        account: viewEntity?.entity?.account?._id,
                    })
                );
            }

            const [signatureUpload, attachmentsUpload] = await Promise.all(uploadPromises);
            const _driverUnLoadingPayload = {
                ...data,
                entity: data?.entity?._id || null,
                account: data?.account?._id || null,
                loadingId: data?.loadingId?._id || null,
                shipmentId,
                unloadingStatus:
                    data.shipmentId.length < (data.loadingId?.shipment?.length || availableShipments.length || 0)
                        ? "Partial"
                        : "Complete",
                signature: signatureUpload.uploadedFiles[0]?.url || "",
                attachments: [
                    ...existingAttachments,
                    ...(attachmentsUpload?.uploadedFiles || []),
                ].map((attachment) => ({
                    name: attachment.name || "",
                    type: attachment.type || "",
                    size: attachment.size || "",
                    publicId: attachment.publicId || "",
                    url: attachment.secureUrl || attachment.url || "",
                })),
                responsible: data?.responsible?.id || null,
            };

            onSubmit(_driverUnLoadingPayload);
        } catch (error) {
            toast.error("Failed to upload files.");
        }
    };

    return (
        <AppForm loading={isBusy}>
            <AppForm.Content>
                <Grid container spacing={4}>
                    {(isAccountAdmin || isSystemAdmin) && (
                        <Grid item md={6}>
                            <Controller
                                name="account"
                                control={control}
                                rules={{ required: "Please select an account." }}
                                render={({ field }) => (
                                    <BaseAutocomplete
                                        {...field}
                                        fullWidth
                                        label="Account"
                                        required
                                        value={field.value ?? null}
                                        onChange={(_, data) => {
                                            field.onChange(data);
                                            setValue("entity", null);
                                            setValue("branch", null);
                                            setValue("loadingId", null);
                                            setValue("shipmentId", []);
                                        }}
                                        getOptionLabel={(option) => option?.name ?? ""}
                                        isOptionEqualToValue={(option, value) =>
                                            option?._id === value?._id || value === null || value === ""
                                        }
                                        options={accounts || []}
                                        loading={loadingAccounts}
                                        loadingText="Loading Account..."
                                        disabled={isAccountAdmin || loadingAccounts}
                                        helperText={errors?.account?.message}
                                        error={!!errors?.account}
                                    />
                                )}
                            />
                        </Grid>
                    )}
                    {(isAccountAdmin || isSystemAdmin) && (
                        <Grid item md={6}>
                            <Controller
                                name="entity"
                                control={control}
                                rules={{ required: "Please select an entity." }}
                                render={({ field: { value, onChange } }) => (
                                    <BaseAutocomplete
                                        fullWidth
                                        required
                                        label="Entity"
                                        value={value ?? null}
                                        onChange={(_, data) => {
                                            onChange(data);
                                            setValue("branch", null);
                                            setValue("loadingId", null);
                                            setValue("shipmentId", []);
                                        }}
                                        getOptionLabel={(option) => option?.name ?? ""}
                                        isOptionEqualToValue={(option, value) =>
                                            option?._id === value?._id || value === null || value === ""
                                        }
                                        options={entities || []}
                                        loading={loadingEntities}
                                        loadingText="Loading Entities..."
                                        disabled={loadingEntities}
                                        helperText={errors?.entity?.message}
                                        error={!!errors?.entity}
                                    />
                                )}
                            />
                        </Grid>
                    )}

                    {/* Branch Field */}
                    <Grid item md={6}>
                        <Controller
                            name="branch"
                            control={control}
                            render={({ field: { value, onChange } }) => (
                                <BaseAutocomplete
                                    fullWidth
                                    label="Branch (Optional)"
                                    value={value ?? null}
                                    onChange={(_, data) => {
                                        onChange(data);
                                        if (data) {
                                            // Populate loadingId from the first loading of the branch
                                            const selectedBranchGroup = branchShipmentLoadings?.data?.find(
                                                (group) => group?.destinationBranch?._id === data._id
                                            );

                                            if (selectedBranchGroup?.loadings?.length) {
                                                setValue("loadingId", selectedBranchGroup.loadings[0], { shouldValidate: true });
                                            } else {
                                                setValue("loadingId", null);
                                            }
                                        } else {
                                            setValue("loadingId", null);
                                        }

                                        // Always reset shipments
                                        setValue("shipmentId", []);
                                    }}

                                    getOptionLabel={(option) => option?.name ?? ""}
                                    isOptionEqualToValue={(option, value) =>
                                        option?._id === value?._id || value === null
                                    }
                                    options={availableBranches}
                                    loading={loadingBranchShipmentLoadings}
                                    loadingText="Loading Branches..."
                                    disabled={loadingBranchShipmentLoadings || !!state?.loading}
                                    helperText={errors?.branch?.message}
                                    error={!!errors?.branch}
                                />
                            )}
                        />
                    </Grid>

                    <Grid item md={6}>
                        <Controller
                            name="loadingId"
                            control={control}
                            rules={{
                                validate: (val) =>
                                    val || _watchBranch || "Please select ShipmentLoading ID or Branch.",
                            }}
                            render={({ field: { value, onChange } }) => (
                                <BaseAutocomplete
                                    fullWidth
                                    label="Loading"
                                    required={!_watchBranch}
                                    value={value ?? null}
                                    onChange={(_, data) => {
                                        onChange(data);
                                        if (data) {
                                            setValue("branch", null);
                                            if (data.shipment) {
                                                setValue("shipmentId", [{
                                                    _id: data.shipment._id,
                                                    shipmentId: data.shipment.shipmentId,
                                                    status: data.shipment.status,
                                                    IsReturning: data.shipment.IsReturning,
                                                    vendor: data.shipment.vendor,
                                                    receiver: data.shipment.receiver,
                                                }], {
                                                    shouldValidate: false,
                                                });
                                            } else {
                                                setValue("shipmentId", [], {
                                                    shouldValidate: false,
                                                });
                                            }
                                        } else {
                                            setValue("shipmentId", [], {
                                                shouldValidate: false,
                                            });
                                        }
                                    }}
                                    getOptionLabel={(option) => option?.loadingId || ""}
                                    isOptionEqualToValue={(option, value) =>
                                        option?.loadingId === value?.loadingId || value === null
                                    }
                                    options={availableLoadings}
                                    loading={loadingBranchShipmentLoadings}
                                    helperText={errors?.loadingId?.message}
                                    error={!!errors?.loadingId}
                                    disabled={!!state?.loading || loadingBranchShipmentLoadings}
                                />
                            )}
                        />
                    </Grid>

                    <Grid item md={6}>
                        <Controller
                            name="shipmentId"
                            control={control}
                            rules={{
                                validate: (value) =>
                                    value.length > 0 || "Please select at least one Shipment ID.",
                            }}
                            render={({ field: { value, onChange } }) => (
                                <BaseAutocomplete
                                    fullWidth
                                    multiple
                                    label="Shipment"
                                    required
                                    value={value ?? []}
                                    onChange={(_, data) => {
                                        onChange(data);
                                        trigger("shipmentId");
                                    }}
                                    getOptionLabel={(option) =>
                                        option?.shipmentId
                                            ? option.IsReturning || option.status === "Return"
                                                ? `${option.shipmentId} (R)`
                                                : option.shipmentId
                                            : ""
                                    }
                                    isOptionEqualToValue={(option, value) =>
                                        option?._id === value?._id
                                    }
                                    options={availableShipments}
                                    loading={loadingBranchShipmentLoadings}
                                    loadingText="Loading Shipments..."
                                    helperText={errors?.shipmentId?.message}
                                    error={!!errors?.shipmentId}
                                    disabled={!!state?.shipments || loadingBranchShipmentLoadings} />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Controller
                            name="responsible"
                            control={control}
                            rules={{ required: "Please select a Responsible User." }}
                            render={({ field }) => (
                                <BaseAutocomplete
                                    fullWidth
                                    required
                                    label="Responsible User"
                                    loading={loadingUsers || fetchingUsers}
                                    loadingText="Loading Responsible Users..."
                                    options={_mappedUsers}
                                    value={field.value || null}
                                    onChange={(_, data) => {
                                        field.onChange(data);
                                    }}
                                    getOptionLabel={(option) => option?.label || ""}
                                    isOptionEqualToValue={(option, value) =>
                                        option?.id === value?.id
                                    }
                                    error={!!errors?.responsible}
                                    helperText={errors?.responsible?.message}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <Controller
                            name="unloadingTime"
                            control={control}
                            defaultValue={INITIAL_STATE.unloadingTime}
                            rules={{ required: "Unloading Time is required" }}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="Unloading Time"
                                    type="datetime-local"
                                    fullWidth
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                    error={!!errors.unloadingTime}
                                    helperText={errors?.unloadingTime?.message}
                                />
                            )}
                        />
                    </Grid>
                    {_watchShipmentId?.length > 0 && (
                        <Grid item sm={12}>
                            <Paper sx={{ p: 2, boxShadow: 2, borderRadius: 2 }}>
                                <Typography variant="h6" gutterBottom>
                                    Shipment Details
                                </Typography>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Shipment</TableCell>
                                            <TableCell>Sender</TableCell>
                                            <TableCell>Receiver</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {_watchShipmentId.map((shipment, index) => {
                                            const selectedShipment =
                                                availableShipments.find(
                                                    (s) => s._id === (shipment._id || shipment)
                                                ) || shipment;

                                            const senderName = selectedShipment?.customerId?.name
                                                ? `CUS: ${selectedShipment.customerId.name}`
                                                : selectedShipment?.vendor?.shopName
                                                    ? `VEN: ${selectedShipment.vendor.shopName}`
                                                    : selectedShipment?.vendor?.contactPerson
                                                        ? `VEN: ${selectedShipment.vendor.contactPerson}`
                                                        : "Not Available";

                                            const receiverName =
                                                selectedShipment?.receiver?.name || "Not Available";
                                            return (
                                                <TableRow key={selectedShipment?._id || index}>
                                                    <TableCell>{selectedShipment?.shipmentId || "Unknown"}</TableCell>
                                                    <TableCell>{senderName}</TableCell>
                                                    <TableCell>{receiverName}</TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </Paper>
                        </Grid>
                    )}
                    <Grid item sm={6}>
                        <div style={{ display: "flex", alignItems: "stretch", height: "100%" }}>
                            <SignaturePadComponent
                                label="Signature"
                                ref={signaturePadRef}
                                InputLabelProps={{ shrink: true }}
                                width={640}
                                height={260}
                            />
                        </div>
                        {signatureError && (
                            <FormHelperText error>Please sign before submitting.</FormHelperText>
                        )}
                    </Grid>
                    <Grid item sm={6}>
                        <FormHelperText>Attachments</FormHelperText>
                        <Controller
                            name="attachments"
                            control={control}
                            render={({ field }) => (
                                <FileDropzone
                                    multiple
                                    maxFiles={5}
                                    files={field?.value || []}
                                    onFileChange={(files) => {
                                        setValue("attachments", files);
                                        trigger("attachments");
                                    }}
                                    disabled={!!driverUnLoadingDetail}
                                    acceptedFileTypes={{
                                        "image/*": [".jpeg", ".jpg", ".png"],
                                        "application/pdf": [".pdf"],
                                    }}
                                    error={!!errors.attachments}
                                    helperText={errors.attachments?.message || ""}
                                    showError={isSubmitted}
                                />
                            )}
                        />
                        <Grid item sm={5} mt={1}>
                            <Controller
                                name="receiveInBehalf"
                                control={control}
                                render={({ field }) => (
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                color="primary"
                                                checked={field.value}
                                                onChange={(e) => {
                                                    field.onChange(e.target.checked);
                                                    if (!e.target.checked) {
                                                        setValue("receiveBy", "");
                                                        setValue("receiverPhone", "");
                                                    }
                                                }}
                                            />
                                        }
                                        label="Receive In Behalf?"
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>
                    {_watchReceiveInBehalf && (
                        <>
                            <Grid item xs={6}>
                                <Controller
                                    name="receiveBy"
                                    control={control}
                                    rules={{ required: "Please provide receiver Name." }}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            required
                                            fullWidth
                                            size="small"
                                            label="Receiver Name"
                                            placeholder="Enter Receiver Name"
                                            error={!!errors?.receiveBy}
                                            helperText={errors?.receiveBy?.message}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <Controller
                                    name="receiverPhone"
                                    control={control}
                                    rules={{
                                        required: "Receiver Phone is required",
                                        validate: (value) => {
                                            const digits = value.replace(/\D/g, "");
                                            if (digits.length < 7 || digits.length > 17) {
                                                return "Phone number must be between 7 and 17 digits";
                                            }
                                            return true;
                                        },
                                    }}
                                    render={({ field, fieldState: { error } }) => (
                                        <TextField
                                            {...field}
                                            required
                                            fullWidth
                                            size="small"
                                            label="Receiver Phone"
                                            placeholder="Enter Receiver Phone"
                                            error={!!error}
                                            helperText={error?.message}
                                        />
                                    )}
                                />
                            </Grid>
                        </>
                    )}
                    {driverUnLoadingDetail && (
                        <Grid item xs={6}>
                            <Controller
                                name="isActive"
                                control={control}
                                render={({ field }) => (
                                    <AppSwitch {...field} label="Is Active?" />
                                )}
                            />
                        </Grid>
                    )}
                </Grid>
            </AppForm.Content>
            <AppForm.Actions>
                <Button onClick={() => navigate(-1)}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit(handleOnDriverUnLoadingSubmit)}
                    disabled={isBusy}
                >
                    {driverUnLoadingDetail ? "Update" : "Add"}
                </Button>
            </AppForm.Actions>
        </AppForm>
    );
};

export default DriverUnLoadingShipmentForm;